import React, { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Select, { components } from "react-select";
import Modal from "react-bootstrap/Modal";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { toastError, toastSuccess } from "../../Common/ToastAlert";
import { AdminContext } from "../../../context/AdminContext";
import {
  addAdminUsers,
  getNetworkByUserId,
  getAllPermissionByRole,
  getAllRoles,
  getAllPermission,
} from "../../../api/admin";
import useFetch from "../../hooks/useFetch";
import customStyles from "../../../assets/sass/Common/selectStyles";
import TextField from "../../Common/TextField";

// --- Custom Multi-Select ---
const CustomValueContainer = ({ children, getValue, ...props }) => {
  const selected = getValue();
  if (selected.length <= 2)
    return (
      <components.ValueContainer {...props}>
        {children}
      </components.ValueContainer>
    );
  return (
    <components.ValueContainer {...props}>
      {children[0].slice(0, 2)}
      <span style={{ fontSize: "12px", color: "#666", marginLeft: "5px" }}>
        +{selected.length - 2}
      </span>
      {children[1]}
    </components.ValueContainer>
  );
};

const OptionWithCheckbox = (props) => (
  <components.Option {...props}>
    <input
      type="checkbox"
      checked={props.isSelected}
      readOnly
      style={{ marginRight: "10px" }}
    />
    {props.label}
  </components.Option>
);

const InviteUser = ({ show, handleClose, refetchData }) => {
  const { userId } = useContext(AdminContext);
  const [submitting, setSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  const selectedRole = watch("role");
  const isCustomRole = selectedRole?.label === "Custom Role";
  const roleId = selectedRole?.value;

  // --- Start API Calls ---
  const { data: networkData, loading: netLoading } = useFetch(
    getNetworkByUserId,
    { userId }
  );
  const { data: rolesData, loading: rolesLoading } = useFetch(getAllRoles);

  // 1. Role-specific permissions
  const { data: rolePermData, loading: rolePermLoading } = useFetch(
    roleId && !isCustomRole ? getAllPermissionByRole : null,
    roleId && !isCustomRole ? { roleId } : null
  );

  // 2. All permissions (sirf Custom Role ke liye)
  const { data: allPermData, loading: allPermLoading } = useFetch(
    isCustomRole ? getAllPermission : null
  );
  // --- End API Calls ---

  useEffect(() => {
    if (rolePermData && !isCustomRole) {
      const formatted = rolePermData.map((p) => ({
        value: p.id,
        label: p.name,
      }));
      setValue("permissions", formatted);
    } else if (isCustomRole) {
      setValue("permissions", []);
    }
  }, [rolePermData, isCustomRole, setValue]);

  // Options Mapping
  const networkOptions =
    networkData?.map((n) => ({ value: n.id, label: n.name })) || [];
  const rawRoleOptions =
    rolesData?.map((r) => ({ value: r.id, label: r.name })) || [];
  const roleOptions = [
    ...rawRoleOptions,
    { value: "custom", label: "Custom Role" },
  ];

  const permOptions = isCustomRole
    ? allPermData?.map((p) => ({ value: p.id, label: p.name })) || []
    : rolePermData?.map((p) => ({ value: p.id, label: p.name })) || [];

  const sendInvitation = async (formData) => {
    setSubmitting(true);
    const payload = {
      email: formData.email.toLowerCase(),
      networkId: formData.consortium.value,
      roleName: isCustomRole ? formData.customRoleName : formData.role.label,
      description: "",
      permissionIds: formData.permissions?.map((p) => p.value) || [],
    };

    try {
      const response = await addAdminUsers(payload);
      if (response.data.success) {
        toast.success("Invite sent successfully", toastSuccess());
        handleClose();
        refetchData();
      } else {
        toast.error(response.data.message || "Failed", toastError());
      }
    } catch (error) {
      toast.error("Something went wrong", toastError());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      className="dsm-modal-popup"
    >
      <Modal.Header closeButton>
        <Modal.Title>Invite User</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Email Field */}
        <div className="mb-3">
          <Controller
            name="email"
            control={control}
            rules={{ required: "Required" }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Email ID"
                placeholder="Enter email ID"
                error={errors.email?.message}
              />
            )}
          />
        </div>

        {/* Consortium Select */}
        <div className="mb-3 textfield-container">
          <label className="inputbox-label">Select Consortium</label>
          <Controller
            name="consortium"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Select
                {...field}
                options={networkOptions}
                isLoading={netLoading}
                styles={customStyles(!!errors.consortium)}
              />
            )}
          />
        </div>

        {/* Role Select */}
        <div className="mb-3 textfield-container">
          <label className="inputbox-label">Select Role</label>
          <Controller
            name="role"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Select
                {...field}
                options={roleOptions}
                isLoading={rolesLoading}
                styles={customStyles(!!errors.role)}
              />
            )}
          />
        </div>

        {/* --- Custom Role Name Input (Conditional) --- */}
        {isCustomRole && (
          <div className="mb-3">
            <Controller
              name="customRoleName"
              control={control}
              rules={{ required: "Custom Role Name is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Custom Role Name"
                  placeholder="Enter a name for the custom role"
                  error={errors.customRoleName?.message}
                />
              )}
            />
          </div>
        )}

        {/* Permissions Multi-Select */}
        <div className="mb-3 textfield-container">
          <label className="inputbox-label">Permissions</label>
          <Controller
            name="permissions"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                isMulti
                options={permOptions}
                isLoading={isCustomRole ? allPermLoading : rolePermLoading}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                isDisabled={!roleId}
                components={{
                  ValueContainer: CustomValueContainer,
                  Option: OptionWithCheckbox,
                }}
                styles={customStyles(!!errors.permissions)}
              />
            )}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="dsm-btn-secondary" onClick={handleClose}>
          Cancel
        </button>
        <button
          className="dsm-btn-primary"
          onClick={handleSubmit(sendInvitation)}
          disabled={submitting}
        >
          {submitting ? <Spinner size="sm" /> : "Send Invite"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default InviteUser;
